import React, { useState, useEffect } from "react";
import {
  ButtonGroup,
  Container,
  Row,
  Col,
  ToggleButton,
  Form,
  InputGroup,
} from "react-bootstrap";
import { toast } from "react-toastify";
import useLogger from "../../util/Logger";
import {
  getTemporaryInsecureValue,
  setTemporaryInsecureValue,
} from "../../data/TemporaryInsecureStore";
import PropertySearchResults from "./PropertySearchResults";
import SpinnerButton from "../shared/Spinnerbutton";
import "./PropertySearch.css";
import { useAuth } from "../../data/AuthHook";
import { useBreadcrumb } from "../../data/BreadcrumbHook";

function PropertySearch() {
  const addressSearch = "Address";
  const postcodeSearch = "Postcode";
  const woSearch = "WorkOrder";
  const wiSearch = "WorkItem";

  const auth = useAuth();
  const logger = useLogger();
  const breadcrumbRepository = useBreadcrumb();
  const [searchType, setSearchType] = useState(addressSearch);
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [searching, setSearching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  setTemporaryInsecureValue("Breadcrumb", []);

  useEffect(() => {
    logger.debug("PropertySearch.useEffect", "Loading previous search state");
    const previousState = getTemporaryInsecureValue("PropertySearch");

    breadcrumbRepository.setBreadcrumb([{ route: "/", name: "search" }]);
    if (previousState) {
      logger.debug(
        "PropertySearch.useEffect",
        "Restoring previous search state"
      );
      setSearchType(previousState.searchType);
      setSearchText(previousState.searchText);
      setPageSize(previousState.pageSize);
      setSearchResults(previousState.searchResults);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchLabels = {
    Address: "Property First Line",
    Postcode: "Postcode",
    WorkOrder: "Work Order ID",
    WorkItem: "Work Item Reference",
  };
  const searchPrompt = searchLabels[searchType];

  const onChangeSearchText = (e) => {
    setSearchText(e.target.value);
  };

  const isEmptyOrSpaces = (str) =>
    str === undefined || str === null || str.match(/^ *$/) !== null;

  const doParameterizedSearch = async (
    theSearchType,
    theSearchText,
    thePageSize
  ) => {
    if (isEmptyOrSpaces(theSearchText) || isEmptyOrSpaces(theSearchType)) {
      logger.debug(
        "PropertySearch.doParameterizedSearch",
        `Resetting search results`
      );
      setSearchResults([]);
      setSearching(false);
      setLoadingMore(false);
    } else {
      logger.debug(
        "PropertySearch.doParameterizedSearch",
        `Starting API search searchType='${searchType}' searchtext=${searchText} pageSize='${thePageSize}'`
      );

      try {
        const requestUrl = `field/search?searchType=${encodeURIComponent(
          searchType
        )}&searchTerm=${encodeURIComponent(
          searchText
        )}&pageSize=${thePageSize}&pageNumber=1`;

        const json = await auth.get(requestUrl);

        setSearchResults(json);
        setTemporaryInsecureValue("PropertySearch", {
          searchType,
          searchText,
          pageSize,
          searchResults: json,
        });
        setSearching(false);
        setLoadingMore(false);
      } catch (error) {
        setSearching(false);
        setLoadingMore(false);

        logger.error(
          "PropertySearch.doParameterizedSearch",
          `API search failed ${error}`
        );

        toast.error(
          "An error occurred while performing the property search. Please try again."
        );
      }
    }
  };

  const doSearch = () => {
    // On press search button, reset page size
    const newPageSize = 10;
    setPageSize(newPageSize);
    setSearching(true);
    doParameterizedSearch(searchType, searchText, newPageSize);
  };

  const detectEnter = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      doSearch();
    }
  };

  const updateSearchType = (newType) => {
    setSearchType(newType);
    setSearchResults([]);
  };

  const showMoreResults = () => {
    const newPageSize = pageSize + 10;
    setPageSize(newPageSize);
    setLoadingMore(true);
    doParameterizedSearch(searchType, searchText, newPageSize);
  };

  return (
    <Container className="coreContent">
      <Row>
        <Col>
          <ButtonGroup>
            <ToggleButton
              id="propertySearch"
              type="radio"
              variant={
                searchType === addressSearch
                  ? "outline-primary"
                  : "outline-secondary"
              }
              name="radio"
              value={addressSearch}
              checked={searchType === addressSearch}
              onChange={() => updateSearchType(addressSearch)}
            >
              Property
            </ToggleButton>
            <ToggleButton
              id="postcodeSearch"
              type="radio"
              variant={
                searchType === postcodeSearch
                  ? "outline-primary"
                  : "outline-secondary"
              }
              name="radio"
              value={postcodeSearch}
              checked={searchType === postcodeSearch}
              onChange={() => updateSearchType(postcodeSearch)}
            >
              Postcode
            </ToggleButton>
            <ToggleButton
              id="woSearch"
              type="radio"
              variant={
                searchType === woSearch
                  ? "outline-primary"
                  : "outline-secondary"
              }
              name="radio"
              value={woSearch}
              checked={searchType === woSearch}
              onChange={() => updateSearchType(woSearch)}
            >
              Work Order
            </ToggleButton>
            <ToggleButton
              id="wiSearch"
              type="radio"
              variant={
                searchType === wiSearch
                  ? "outline-primary"
                  : "outline-secondary"
              }
              name="radio"
              value={wiSearch}
              checked={searchType === wiSearch}
              onChange={() => updateSearchType(wiSearch)}
            >
              Work Item
            </ToggleButton>
          </ButtonGroup>
        </Col>
      </Row>
      <Row className="mt-2 search-form">
        <Col>
          <Form>
            <Form.Group className="mb-3" controlId="searchForm.SearchText">
              <Form.Label>Search For</Form.Label>
              <InputGroup>
                <Form.Control
                  type="search"
                  placeholder={searchPrompt}
                  value={searchText}
                  onChange={(e) => onChangeSearchText(e)}
                  onKeyPress={(e) => detectEnter(e)}
                />
                <SpinnerButton
                  text="Search"
                  variant="primary"
                  action={doSearch}
                  waiting={searching}
                />
              </InputGroup>
            </Form.Group>
          </Form>
        </Col>
      </Row>
      <Row className="mt-4">
        <Col>
          <PropertySearchResults
            searchResults={searchResults}
            type={searchType}
          />
        </Col>
      </Row>
      <Row className="mt-1">
        <Col>
          <SpinnerButton
            text="More..."
            variant="secondary"
            action={showMoreResults}
            waiting={loadingMore}
          />
        </Col>
      </Row>
    </Container>
  );
}

export default PropertySearch;
